/**
 * Ресурсы проекта: инструменты, аккаунты, источники.
 *
 * Раздел отвечает на организационные вопросы: чем пользуется проект, кто
 * имеет доступ и что случится, если человек уйдёт. Полей с секретами здесь
 * нет — только название записи во внешнем менеджере паролей.
 */

import {
  ExternalLinkIcon,
  KeyRoundIcon,
  LinkIcon,
  ShieldAlertIcon,
  WrenchIcon,
} from 'lucide-react';
import { useState } from 'react';
import { Link, useSearchParams } from 'react-router';
import type { AccountRowDto, ItemRefDto, SourceRowDto, ToolRowDto } from '@project-os/contracts';
import { formatMoney, getItemTypeDefinition } from '@project-os/contracts';
import { useProjectMembers } from '@/api/members';
import { useResources } from '@/api/resources';
import { ItemStatusBadge } from '@/components/common/status-badge';
import { CardListSkeleton, EmptyState, ErrorState } from '@/components/common/states';
import { PageBody, PageHeader } from '@/components/layout/PageHeader';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Card, CardContent } from '@/components/ui/card';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { UserAvatar } from '@/components/common/user-avatar';
import { formatDate } from '@/lib/format';
import { ItemDetailPanel } from '../items/ItemDetailPanel';
import { ItemTypeIcon } from '../items/item-icon';
import { useProjectContext } from '../project/use-project-context';
import { useWorkstreams } from '@/api/workstreams';

export function ResourcesPage() {
  const project = useProjectContext();
  const [searchParams, setSearchParams] = useSearchParams();
  const { data, isPending, isError, error, refetch } = useResources(project.id);
  const { data: workstreams = [] } = useWorkstreams(project.id);
  const { data: members = [] } = useProjectMembers(project.id);
  const [tab, setTab] = useState(searchParams.get('tab') ?? 'tools');

  const selectedId = searchParams.get('item');
  const openItem = (itemId: string) =>
    setSearchParams(
      (previous) => {
        const next = new URLSearchParams(previous);
        next.set('item', itemId);
        return next;
      },
      { replace: true },
    );

  const canEdit = project.permissions.includes('ITEM_WRITE');

  return (
    <>
      <PageHeader title="Ресурсы" description="Чем пользуется проект и у кого есть доступ" />

      <PageBody className="space-y-4">
        {isPending ? (
          <CardListSkeleton count={2} />
        ) : isError ? (
          <ErrorState error={error} onRetry={() => void refetch()} />
        ) : (
          <>
            {data.accountsWithoutOwner.length > 0 ? (
              <Alert>
                <ShieldAlertIcon className="size-4" />
                <AlertDescription className="text-xs leading-relaxed">
                  <span>
                    {data.accountsWithoutOwner.length === 1 ? 'Аккаунт' : 'Аккаунты'} без
                    ответственного:{' '}
                    {data.accountsWithoutOwner.map((account, index) => (
                      <span key={account.id}>
                        {index > 0 ? ', ' : ''}
                        <button
                          type="button"
                          className="text-primary hover:underline"
                          onClick={() => openItem(account.id)}
                        >
                          {account.title}
                        </button>
                      </span>
                    ))}
                    . Если человек уйдёт, восстанавливать доступ будет некому.
                  </span>
                </AlertDescription>
              </Alert>
            ) : null}

            <Tabs
              value={tab}
              onValueChange={(value) => {
                setTab(value);
                setSearchParams(
                  (previous) => {
                    const next = new URLSearchParams(previous);
                    next.set('tab', value);
                    return next;
                  },
                  { replace: true },
                );
              }}
            >
              <TabsList>
                <TabsTrigger value="tools">
                  <WrenchIcon className="size-3.5" />
                  Инструменты
                  <Count value={data.tools.length} />
                </TabsTrigger>
                <TabsTrigger value="accounts">
                  <KeyRoundIcon className="size-3.5" />
                  Аккаунты
                  <Count value={data.accounts.length} />
                </TabsTrigger>
                <TabsTrigger value="sources">
                  <LinkIcon className="size-3.5" />
                  Источники
                  <Count value={data.sources.length} />
                </TabsTrigger>
              </TabsList>

              <TabsContent value="tools" className="mt-3">
                <ToolsTable rows={data.tools} onOpen={openItem} />
              </TabsContent>

              <TabsContent value="accounts" className="mt-3">
                <AccountsTable rows={data.accounts} onOpen={openItem} />
              </TabsContent>

              <TabsContent value="sources" className="mt-3">
                <SourcesList rows={data.sources} onOpen={openItem} projectId={project.id} />
              </TabsContent>
            </Tabs>
          </>
        )}
      </PageBody>

      <ItemDetailPanel
        projectId={project.id}
        itemId={selectedId}
        onClose={() =>
          setSearchParams(
            (previous) => {
              const next = new URLSearchParams(previous);
              next.delete('item');
              return next;
            },
            { replace: true },
          )
        }
        onOpenItem={openItem}
        canEdit={canEdit}
        canReview={project.permissions.includes('REVIEW_WRITE')}
        workstreams={workstreams}
        members={members.map((member) => member.user)}
      />
    </>
  );
}

function Count({ value }: { value: number }) {
  if (value === 0) return null;
  return <span className="ml-1 text-muted-foreground tabular-nums">{value}</span>;
}

/** Ссылки на связанные элементы: открывают карточку, а не уводят со страницы. */
function RefList({ refs, onOpen }: { refs: ItemRefDto[]; onOpen: (id: string) => void }) {
  if (refs.length === 0) return <span className="text-muted-foreground/70">—</span>;
  return (
    <span className="flex flex-wrap gap-x-2 gap-y-0.5">
      {refs.map((ref) => (
        <button
          key={ref.id}
          type="button"
          onClick={() => onOpen(ref.id)}
          className="flex items-center gap-1 text-xs hover:underline"
        >
          <ItemTypeIcon type={ref.type} className="size-3" />
          <span className="max-w-40 truncate">{ref.title}</span>
        </button>
      ))}
    </span>
  );
}

function ToolsTable({ rows, onOpen }: { rows: ToolRowDto[]; onOpen: (id: string) => void }) {
  if (rows.length === 0) {
    return (
      <EmptyState
        icon={<WrenchIcon />}
        title="Инструментов пока нет"
        description={getItemTypeDefinition('TOOL').emptyStateRu}
      />
    );
  }

  return (
    <Card className="py-0">
      <CardContent className="px-0">
        <Table className="table-fixed">
          <TableHeader>
            <TableRow>
              <TableHead className="w-52 pl-4">Название</TableHead>
              <TableHead>Назначение</TableHead>
              <TableHead className="hidden w-32 2xl:table-cell">Поставщик</TableHead>
              <TableHead className="w-36">Ответственный</TableHead>
              <TableHead className="w-32">Статус</TableHead>
              <TableHead className="hidden w-32 xl:table-cell">Тарификация</TableHead>
              <TableHead className="w-44">Аккаунты</TableHead>
              <TableHead className="w-28 pr-4 text-right">Расходы</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {rows.map((row) => (
              <TableRow key={row.item.id} className="cursor-pointer">
                <TableCell className="pl-4" onClick={() => onOpen(row.item.id)}>
                  <span className="flex items-center gap-1.5">
                    <ItemTypeIcon type="TOOL" className="size-3.5 shrink-0" />
                    <span className="min-w-0 truncate font-medium">{row.item.title}</span>
                    {row.serviceUrl ? (
                      <a
                        href={row.serviceUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        onClick={(event) => event.stopPropagation()}
                        className="shrink-0 text-muted-foreground hover:text-foreground"
                        aria-label={`Открыть ${row.item.title}`}
                      >
                        <ExternalLinkIcon className="size-3" />
                      </a>
                    ) : null}
                  </span>
                </TableCell>
                <TableCell
                  className="text-xs text-muted-foreground"
                  onClick={() => onOpen(row.item.id)}
                >
                  <span className="block truncate">{row.purpose ?? '—'}</span>
                </TableCell>
                <TableCell
                  className="hidden text-xs 2xl:table-cell"
                  onClick={() => onOpen(row.item.id)}
                >
                  <span className="block truncate">{row.vendor ?? '—'}</span>
                </TableCell>
                <TableCell className="text-xs" onClick={() => onOpen(row.item.id)}>
                  <OwnerCell owner={row.item.owner} />
                </TableCell>
                <TableCell onClick={() => onOpen(row.item.id)}>
                  <ItemStatusBadge type="TOOL" status={row.item.status} />
                </TableCell>
                <TableCell
                  className="hidden text-xs xl:table-cell"
                  onClick={() => onOpen(row.item.id)}
                >
                  {row.billingCycleLabelRu ?? '—'}
                </TableCell>
                <TableCell>
                  <RefList refs={row.linkedAccounts} onOpen={onOpen} />
                </TableCell>
                <TableCell className="pr-4 text-right text-xs tabular-nums">
                  {row.expenses.length === 0 ? (
                    <span className="text-muted-foreground/70">—</span>
                  ) : (
                    <span className="flex flex-col items-end">
                      {row.expenses.map((total) => (
                        <span key={total.currency}>{formatMoney(total)}</span>
                      ))}
                    </span>
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </CardContent>
    </Card>
  );
}

function AccountsTable({ rows, onOpen }: { rows: AccountRowDto[]; onOpen: (id: string) => void }) {
  if (rows.length === 0) {
    return (
      <EmptyState
        icon={<KeyRoundIcon />}
        title="Аккаунтов пока нет"
        description={getItemTypeDefinition('ACCOUNT').emptyStateRu}
      />
    );
  }

  return (
    <div className="space-y-3">
      <Card className="py-0">
        <CardContent className="px-0">
          <Table className="table-fixed">
            <TableHeader>
              <TableRow>
                <TableHead className="w-52 pl-4">Сервис</TableHead>
                <TableHead className="w-48">Логин</TableHead>
                <TableHead className="w-40">Ответственный</TableHead>
                <TableHead>Доступ есть у</TableHead>
                <TableHead className="w-44">Где лежит доступ</TableHead>
                <TableHead className="w-32">Ревизия</TableHead>
                <TableHead className="w-28 pr-4">Статус</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {rows.map((row) => (
                <TableRow key={row.item.id} className="cursor-pointer">
                  <TableCell className="pl-4" onClick={() => onOpen(row.item.id)}>
                    <span className="flex items-center gap-1.5">
                      <ItemTypeIcon type="ACCOUNT" className="size-3.5 shrink-0" />
                      <span className="min-w-0 truncate font-medium">
                        {row.serviceName ?? row.item.title}
                      </span>
                    </span>
                  </TableCell>
                  <TableCell
                    className="text-xs text-muted-foreground"
                    onClick={() => onOpen(row.item.id)}
                  >
                    <span className="block truncate">{row.loginEmail ?? '—'}</span>
                  </TableCell>
                  <TableCell className="text-xs" onClick={() => onOpen(row.item.id)}>
                    <OwnerCell owner={row.item.owner} />
                  </TableCell>
                  <TableCell
                    className="overflow-hidden text-xs"
                    onClick={() => onOpen(row.item.id)}
                  >
                    {row.accessMembers.length === 0 ? (
                      <span className="text-muted-foreground/70">никого</span>
                    ) : (
                      <span className="flex flex-wrap gap-1">
                        {row.accessMembers.map((access) => (
                          <span
                            key={access.id}
                            className="max-w-full truncate rounded bg-muted px-1.5 py-0.5 text-2xs"
                            title={`${access.labelRu}${access.notes ? ` · ${access.notes}` : ''}`}
                          >
                            {access.user.name}
                            <span className="text-muted-foreground"> · {access.labelRu}</span>
                          </span>
                        ))}
                      </span>
                    )}
                  </TableCell>
                  <TableCell
                    className="text-xs text-muted-foreground"
                    onClick={() => onOpen(row.item.id)}
                  >
                    <span className="block truncate">{row.credentialReference ?? '—'}</span>
                  </TableCell>
                  <TableCell className="text-xs" onClick={() => onOpen(row.item.id)}>
                    {row.lastAccessReviewAt ? formatDate(row.lastAccessReviewAt) : '—'}
                  </TableCell>
                  <TableCell className="pr-4" onClick={() => onOpen(row.item.id)}>
                    <ItemStatusBadge type="ACCOUNT" status={row.item.status} />
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      <p className="text-2xs leading-relaxed text-muted-foreground/80">
        Паролей, ключей и recovery-кодов в системе нет. «Где лежит доступ» — это название записи во
        внешнем менеджере паролей, а список доступа отвечает на вопрос «кто сможет войти, если
        человек уйдёт».
      </p>
    </div>
  );
}

function SourcesList({
  rows,
  onOpen,
  projectId,
}: {
  rows: SourceRowDto[];
  onOpen: (id: string) => void;
  projectId: string;
}) {
  if (rows.length === 0) {
    return (
      <EmptyState
        icon={<LinkIcon />}
        title="Источников пока нет"
        description={getItemTypeDefinition('SOURCE').emptyStateRu}
      />
    );
  }

  return (
    <ul className="space-y-2">
      {rows.map((row) => (
        <li key={row.item.id} className="surface-card border border-border/50 p-3">
          <div className="flex items-start gap-2">
            <ItemTypeIcon type="SOURCE" className="mt-0.5 size-3.5 shrink-0" />
            <div className="min-w-0 flex-1">
              <button
                type="button"
                onClick={() => onOpen(row.item.id)}
                className="text-left text-sm font-medium hover:underline"
              >
                {row.item.title}
              </button>
              <p className="mt-0.5 flex flex-wrap gap-x-2 text-2xs text-muted-foreground">
                {row.sourceTypeLabelRu ? <span>{row.sourceTypeLabelRu}</span> : null}
                {row.publishedAt ? <span>{formatDate(row.publishedAt)}</span> : null}
                {row.url ? (
                  <a
                    href={row.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-1 text-primary hover:underline"
                  >
                    открыть
                    <ExternalLinkIcon className="size-2.5" />
                  </a>
                ) : null}
              </p>
            </div>
            <ItemStatusBadge type="SOURCE" status={row.item.status} />
          </div>

          {row.referencedBy.length > 0 ? (
            <div className="mt-2 border-t border-border/40 pt-2">
              <p className="mb-1 text-2xs text-muted-foreground">
                На источник ссылаются ({row.referencedBy.length}):
              </p>
              <RefList refs={row.referencedBy} onOpen={onOpen} />
            </div>
          ) : (
            <p className="mt-2 border-t border-border/40 pt-2 text-2xs text-muted-foreground/70">
              На источник пока никто не ссылается.{' '}
              <Link to={`/projects/${projectId}/items?type=SOURCE`} className="hover:underline">
                Открыть в списке
              </Link>
            </p>
          )}
        </li>
      ))}
    </ul>
  );
}

function OwnerCell({ owner }: { owner: { name: string } | null }) {
  if (!owner) return <span className="text-tone-warning">не назначен</span>;
  return (
    <span className="flex items-center gap-1.5">
      <UserAvatar user={owner} size="sm" />
      <span className="truncate">{owner.name}</span>
    </span>
  );
}
